import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static targets = ["modal", "overlay"];
  static values = {
    hideMain: { type: Boolean, default: false },
    closeOnClickOutside: { type: Boolean, default: false }
  };

  declare modalTarget: HTMLElement;
  declare hasModalTarget: boolean;
  declare overlayTarget: HTMLElement;
  declare hasOverlayTarget: boolean;
  declare hideMainValue: boolean;
  declare closeOnClickOutsideValue: boolean;

  private boundClickOutside: ((event: MouseEvent) => void) | null = null;
  private boundKeydown: ((event: KeyboardEvent) => void) | null = null;
  private previouslyFocused: HTMLElement | null = null;

  open(event?: Event): void {
    if (event) event.preventDefault();

    if (this.hideMainValue) {
      const main = document.querySelector('main');
      if (main) main.style.display = 'none';
    }

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('hidden');
    }

    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden');
      this.modalTarget.style.display = 'flex';
      // Expose dialog semantics if the template didn't already.
      if (!this.modalTarget.hasAttribute('role')) {
        this.modalTarget.setAttribute('role', 'dialog');
      }
      this.modalTarget.setAttribute('aria-modal', 'true');
      // Fallback focus target when the dialog has no focusable children.
      if (!this.modalTarget.hasAttribute('tabindex')) {
        this.modalTarget.setAttribute('tabindex', '-1');
      }
    }

    // Move focus into the dialog and remember where it came from (WCAG 2.4.3).
    this.previouslyFocused = document.activeElement as HTMLElement;
    const focusable = this.getFocusable();
    (focusable[0] ?? this.modalTarget).focus();

    // Escape-to-close + focus trap while open (WCAG 2.1.2 No Keyboard Trap).
    this.boundKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.boundKeydown);

    if (this.closeOnClickOutsideValue) {
      this.boundClickOutside = this.clickOutside.bind(this);
      document.addEventListener('click', this.boundClickOutside);
    }
  }

  close(event?: Event): void {
    if (event) event.preventDefault();

    if (this.hideMainValue) {
      const main = document.querySelector('main');
      if (main) main.style.display = '';
    }

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add('hidden');
    }

    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden');
      this.modalTarget.style.display = 'none';
      this.modalTarget.removeAttribute('aria-modal');
    }

    if (this.boundKeydown) {
      document.removeEventListener('keydown', this.boundKeydown);
      this.boundKeydown = null;
    }

    if (this.boundClickOutside) {
      document.removeEventListener('click', this.boundClickOutside);
      this.boundClickOutside = null;
    }

    // Return focus to whatever opened the dialog (WCAG 2.4.3).
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }
    this.previouslyFocused = null;
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = this.getFocusable();
    if (focusable.length === 0) {
      // Nothing to tab to — keep focus on the dialog.
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === this.modalTarget)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private getFocusable(): HTMLElement[] {
    if (!this.hasModalTarget) return [];
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), ' +
      'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(
      this.modalTarget.querySelectorAll<HTMLElement>(selector)
      // Visible only: getClientRects() is empty for display:none and, unlike
      // offsetParent, stays correct for position:fixed dialogs.
    ).filter((el) => el.getClientRects().length > 0);
  }

  clickOutside(event: MouseEvent): void {
    if (this.hasModalTarget && !this.modalTarget.contains(event.target as Node)) {
      this.close();
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
